import { useCallback, useState } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import {
	APP_TEAM_NAME,
	useExperienceTracker,
} from '@atlassian/jira-project-create-importer-common';

import { APP_IDENTIFIER } from '../../common/constants';
import {
	EXPERIENCES,
	thirdPartyFieldConfigInteractionMetric,
} from '../../common/constants/metrics';
import type { ProjectData, ThirdPartyEntity } from '../../common/types';
import { useThirdPartyClient } from '../../common/utils/third-party-clients';
import type { UseThirdPartyFieldConfig, UseThirdPartyFieldConfigProps } from './types';
import { transformToThirdPartyFieldConfigState } from './utils';

export const useThirdPartyFieldConfig = ({
	thirdParty,
	requestAuthenticationToken,
}: UseThirdPartyFieldConfigProps): UseThirdPartyFieldConfig => {
	const thirdPartyClient = useThirdPartyClient({
		thirdParty,
		requestAuthenticationToken,
	});
	const [onExperienceStart, onExperienceSuccess, onExperienceFail] = useExperienceTracker(
		EXPERIENCES.THIRD_PARTY_FIELD_CONFIG,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [hasErrorOccurred, setHasErrorOccurred] = useState(false);

	const fetchThirdPartyFieldConfig = useCallback(
		// IMP-498: Remove entityId and use entities
		async (entityId: string, projectData: ProjectData, entities?: ThirdPartyEntity[]) => {
			setIsLoading(true);
			setHasErrorOccurred(false);
			thirdPartyFieldConfigInteractionMetric.start();
			onExperienceStart();

			try {
				const data = await thirdPartyClient.getFieldConfig({
					entityId,
					// Default name to empty string since we only use the id
					// IMP-498: Remove the fallback  default value
					entities: entities ?? [{ id: entityId, name: '' }],
					projectData,
				});
				const fieldConfigState = transformToThirdPartyFieldConfigState(data);
				thirdPartyFieldConfigInteractionMetric.stop();
				onExperienceSuccess();

				setIsLoading(false);
				return fieldConfigState;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				thirdPartyFieldConfigInteractionMetric.stop();
				onExperienceFail(APP_IDENTIFIER, error, { thirdParty });
				fireErrorAnalytics({
					meta: {
						id: 'fetch-third-party-field-config',
						packageName: APP_IDENTIFIER,
						teamName: APP_TEAM_NAME,
					},
					sendToPrivacyUnsafeSplunk: true,
					error,
				});

				setHasErrorOccurred(true);
				setIsLoading(false);
				return null;
			}
		},
		[onExperienceFail, onExperienceStart, onExperienceSuccess, thirdPartyClient, thirdParty],
	);

	return { isLoading, hasErrorOccurred, fetchThirdPartyFieldConfig };
};
